import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguageAwareRender } from '../hooks/useLanguageAwareRender';
import { t } from '../utils/localization/localization';
import { useRecommendations } from '../hooks/useRecommendations';
import { usePermissionCheck } from '../hooks/usePermissionCheck';
import { logger } from '../utils/logger';
import RecommendationSection from '../components/recommendations/RecommendationSection';
import { LoadingState, ErrorState, EmptyState } from '../components/common';

// Material UI components
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SchoolIcon from '@mui/icons-material/School';
import AssignmentIcon from '@mui/icons-material/Assignment';

export default function ResultPage({ userId: propUserId, hideBackButton = false }) {
  const navigate = useNavigate();
  const { userId: paramUserId } = useParams();
  const { currentUser } = useAuth();

  // Use prop if provided, otherwise use URL parameter, otherwise use current user's ID
  const userId = propUserId || paramUserId || currentUser?.id;

  // Use custom hook for language-aware rendering
  const languageRenderKey = useLanguageAwareRender('ResultPage');

  // State for navigation
  const [openDialog, setOpenDialog] = useState(false);

  // Use custom hooks
  const {
    hasViewPermission,
    teamMemberName,
    permissionError,
    loading: permissionLoading
  } = usePermissionCheck(userId);

  // Enhanced logging for permission debugging
  React.useEffect(() => {
    logger.info('TRAINING_RECOMMENDATIONS_PERMISSION', 'Permission check results', {
      userId,
      currentUserId: currentUser?.id,
      currentUserRole: currentUser?.role,
      hasViewPermission,
      permissionError,
      permissionLoading,
      teamMemberName
    });
  }, [
    userId,
    currentUser?.id,
    currentUser?.role,
    hasViewPermission,
    permissionError,
    permissionLoading,
    teamMemberName
  ]);

  const {
    loading,
    error,
    setError,
    success,
    recommendations,
    viewState,
    generateRecommendations,
    _loadRecommendations
  } = useRecommendations(userId, hasViewPermission);

  // Handle back button
  const handleBack = () => {
    logger.info('ResultPage', 'Back button clicked');
    if (viewState === 'results' && recommendations) {
      // If viewing results, ask for confirmation
      setOpenDialog(true);
    } else {
      // If in initial state, go back directly
      navigate(-1);
    }
  };

  // Handle confirmation of going back
  const handleConfirmBack = () => {
    logger.info('ResultPage', 'Confirmed going back');
    setOpenDialog(false);
    navigate(-1);
  };

  // Show permission error if needed - simplified to match CV and Job analyzer behavior
  if (!permissionLoading && !hasViewPermission && userId !== currentUser?.id && currentUser?.role !== 'admin') {
    return (
      <Box sx={{ p: 3 }}>
        <ErrorState
          error={permissionError || t('common.noPermission')}
          context="Permission check"
          severity="warning"
          showIcon={true}
          component="ResultPage-Permission"
        />
      </Box>
    );
  }

  // Show loading while checking permissions
  if (permissionLoading) {
    return (
      <LoadingState
        message={t('common.loading')}
        fullHeight={true}
        component="ResultPage-PermissionCheck"
      />
    );
  }

  // Render the recommendations content
  const renderRecommendationsContent = () => {
    if (!recommendations) {
      logger.warn('ResultPage', 'No recommendations available to display');
      return (
        <EmptyState
          title={t('result.noRecommendations')}
          message={t('result.noRecommendationsDescription', 'No training recommendations have been generated yet.')}
          icon={AssignmentIcon}
          component="ResultPage-NoRecommendations"
        />
      );
    }

    // Extract different types of recommendations
    const immediateRecs = recommendations.immediate_recommendations || recommendations.immediate || [];
    const futureRecs = recommendations.future_recommendations || recommendations.future || [];
    const aiRecs = recommendations.ai_augmentation_recommendations || recommendations.ai || [];

    // Check if all recommendation arrays are empty
    if (immediateRecs.length === 0 && futureRecs.length === 0 && aiRecs.length === 0) {
      logger.warn('ResultPage', 'All recommendation arrays are empty');
      return (
        <EmptyState
          title={t('result.noRecommendations')}
          message={t('result.emptyRecommendationsDescription', 'No specific recommendations were generated. Please try regenerating.')}
          icon={AssignmentIcon}
          actions={[
            {
              label: t('result.regenerate', 'Regenerate'),
              onClick: generateRecommendations,
              variant: 'contained'
            }
          ]}
          component="ResultPage-EmptyRecommendations"
        />
      );
    }

    return (
      <Box>
        <RecommendationSection titleKey="immediateRecommendations" recommendations={immediateRecs} />
        <RecommendationSection titleKey="futureRecommendations" recommendations={futureRecs} />
        <RecommendationSection titleKey="aiRecommendations" recommendations={aiRecs} />
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }} key={languageRenderKey}>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        {/* Centered title */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" align="center">
            {t('result.title')}
          </Typography>
          {teamMemberName && (
            <Typography variant="subtitle1" color="text.secondary" align="center">
              {t('teamManagement.for')} {teamMemberName}
            </Typography>
          )}
        </Box>

        {error && (
          <Box sx={{ mb: 2 }}>
            <ErrorState
              error={error}
              message={error}
              severity="error"
              onRetry={() => setError('')}
              component="ResultPage-Error"
            />
          </Box>
        )}

        {success && (
          <Box sx={{ mb: 2 }}>
            <ErrorState
              message={success}
              severity="success"
              showRetry={false}
              component="ResultPage-Success"
            />
          </Box>
        )}

        {viewState === 'initial' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5 }}>
            <SchoolIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              {t('result.generatePrompt') || 'Generate Training Recommendations'}
            </Typography>
            <Typography variant="body1" color="text.secondary" align="center" paragraph>
              {t('result.generateDescription') || 'Before generating training recommendations, please ensure you have completed CV Analysis, Job Analysis, and Company Research.'}
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={generateRecommendations}
              disabled={loading}
            >
              {loading ? t('common.loading') : (t('result.generateButton') || 'Generate Recommendations')}
            </Button>
          </Box>
        ) : (
          <Box>
            {loading ? (
              <LoadingState
                message={t('result.loadingRecommendations', 'Loading training recommendations...')}
                size="large"
                showDelay={true}
                component="ResultPage-LoadingRecommendations"
              />
            ) : (
              <>
                {renderRecommendationsContent()}

                {/* Action buttons - back (lower left) and regenerate (lower right) */}
                {recommendations && (
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                    {!hideBackButton && (
                      <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBack}
                      >
                        {t('common.back')}
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      onClick={generateRecommendations}
                      disabled={loading}
                    >
                      {t('result.regenerate') || 'Regenerate'}
                    </Button>
                  </Box>
                )}

                {/* Back button only (when no recommendations) */}
                {!recommendations && !hideBackButton && (
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-start' }}>
                    <Button
                      variant="outlined"
                      startIcon={<ArrowBackIcon />}
                      onClick={handleBack}
                    >
                      {t('common.back')}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('result.confirmBackDialogTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('result.confirmBackDialogContent')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleConfirmBack} autoFocus>
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
